// src/lib/shopify-cart-compat.js
import { createStorefrontApiClient } from '@shopify/storefront-api-client'

const API_VERSION = process.env.SHOPIFY_ADMIN_API_VERSION || '2024-10'

export const sfClient = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_SHOP_NAME + '.myshopify.com',
  publicAccessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
  apiVersion: API_VERSION,
})

const gql = String.raw
const toGid = variantId => {
  return String(variantId).startsWith('gid://') ? variantId : `gid://shopify/ProductVariant/${ variantId }`
}

// ---- Compatibility surface that mirrors old checkout usage ----
export const checkoutCompat = {
  // checkout.create()  -> cartCreate
  async create () {
    const mutation = gql`
      mutation CartCreate {
        cartCreate(input: {}) {
          cart { id checkoutUrl }
          userErrors { field message }
        }
      }
    `
    const { data, errors } = await sfClient.request(mutation)
    if (errors?.length) throw new Error(JSON.stringify(errors))
    const cart = data?.cartCreate?.cart
    if (!cart) throw new Error('Cart not created')
    return { id: cart.id, webUrl: cart.checkoutUrl }
  },

  // checkout.fetch(id) -> cart(id)
  async fetch (checkoutId) {
    const query = gql`
      query Cart($id: ID!) {
        cart(id: $id) {
          id
          checkoutUrl
          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }
          discountCodes { code applicable }
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                attributes { key value }
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price { amount currencyCode }
                    product { title handle }
                    image { url altText }
                  }
                }
              }
            }
          }
        }
      }
    `
    const { data, errors } = await sfClient.request(query, { variables: { id: checkoutId } })
    if (errors?.length) throw new Error(JSON.stringify(errors))
    const cart = data?.cart
    if (!cart) throw new Error('Cart not found')
    // keep a familiar shape
    return {
      id: cart.id,
      webUrl: cart.checkoutUrl,
      cost: cart.cost,
      discountCodes: cart.discountCodes,
      lineItems: cart.lines?.edges?.map(e => e.node) || [],
      _raw: cart,
    }
  },

  // checkout.addLineItems(id, [{variantId, quantity, customAttributes}])
  async addLineItems (checkoutId, lineItems) {
    const mutation = gql`
      mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { id }
          userErrors { field message }
        }
      }
    `
    const lines = lineItems.map(li => ({
      merchandiseId: toGid(li.variantId),
      quantity: li.quantity || 1,
      attributes: li.customAttributes,
    }))
    const { data, errors } = await sfClient.request(mutation, {
      variables: { cartId: checkoutId, lines },
    })
    const uErr = data?.cartLinesAdd?.userErrors
    if (errors?.length || uErr?.length) throw new Error(JSON.stringify(errors || uErr))
    return this.fetch(checkoutId)
  },

  // checkout.updateLineItems(id, [{id, quantity}])  (uses cart line IDs)
  async updateLineItems (checkoutId, lineItems) {
    const mutation = gql`
      mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { id }
          userErrors { field message }
        }
      }
    `
    const { data, errors } = await sfClient.request(mutation, {
      variables: { cartId: checkoutId, lines: lineItems },
    })
    const uErr = data?.cartLinesUpdate?.userErrors
    if (errors?.length || uErr?.length) throw new Error(JSON.stringify(errors || uErr))
    return this.fetch(checkoutId)
  },

  // checkout.removeLineItems(id, [lineItemId])  (uses cart line IDs)
  async removeLineItems (checkoutId, lineItemIds) {
    const mutation = gql`
      mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { id }
          userErrors { field message }
        }
      }
    `
    const { data, errors } = await sfClient.request(mutation, {
      variables: { cartId: checkoutId, lineIds: lineItemIds },
    })
    const uErr = data?.cartLinesRemove?.userErrors
    if (errors?.length || uErr?.length) throw new Error(JSON.stringify(errors || uErr))
    return this.fetch(checkoutId)
  },

  // checkout.addDiscount(id, code)  -> cartDiscountCodesUpdate
  async addDiscount (checkoutId, discountCode) {
    const mutation = gql`
      mutation CartDiscountCodesUpdate($cartId: ID!, $codes: [String!]!) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {
          cart { id }
          userErrors { field message }
        }
      }
    `
    const { data, errors } = await sfClient.request(mutation, {
      variables: { cartId: checkoutId, codes: [discountCode] },
    })
    const uErr = data?.cartDiscountCodesUpdate?.userErrors
    if (errors?.length || uErr?.length) throw new Error(JSON.stringify(errors || uErr))
    return this.fetch(checkoutId)
  },

  // checkout.removeDiscount(id)
  async removeDiscount (checkoutId) {
    const mutation = gql`
      mutation CartDiscountCodesClear($cartId: ID!) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: [])
      }
    `
    const { errors } = await sfClient.request(mutation, { variables: { cartId: checkoutId } })
    if (errors?.length) throw new Error(JSON.stringify(errors))
    return this.fetch(checkoutId)
  },
}

// NOTE:
// - Setting shipping/billing BEFORE redirect isn’t supported by Cart API.
//   Those are collected on Shopify’s checkout page via checkoutUrl.
